"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-8">
        <div className="h-4 w-20 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-4 w-20 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-4 w-20 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-4 w-20 bg-muted/40 animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem data-index={i} key={item.id}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative inline-flex h-auto items-center justify-center bg-transparent px-0 py-1 text-sm font-medium tracking-wide text-foreground hover:bg-transparent hover:text-foreground focus:bg-transparent focus:text-foreground after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-primary after:transition-all after:duration-300 after:ease-out hover:after:w-full">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 px-6">
        <div className="h-8 w-40 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-8 w-40 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-8 w-40 bg-muted/40 animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-2 px-6">
      {menuItems.map((item, i) => <Link data-index={i} key={item.id} to={item.href || "#"} onClick={onNavigate} className="font-default text-3xl font-normal tracking-tight text-foreground py-3 border-b border-border/60 hover:text-primary transition-colors duration-300">
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="14889" id="header" className={`sticky top-0 z-50 w-full bg-background text-foreground transition-all duration-300 ease-out ${scrolled ? "bg-background/80 backdrop-blur-md border-b border-border shadow-sm" : "bg-background border-b border-border"}`}>
      <div className="mx-auto w-full max-w-[1440px] px-6 md:px-8">
        <div className="flex h-[60px] md:h-[72px] items-center justify-between">
          {}
          <div className="flex items-center">
            <Link to="/HomePage" aria-label="Acumen D&B home">
              <WvcLogo className="h-7 w-auto" />
            </Link>
          </div>

          {}
          <div className="hidden md:block flex-1" aria-hidden="true" />

          {}
          <div className="hidden md:flex items-center gap-8">
            <MenuProvider menu_id="16">
              <DesktopMenuConsumer />
            </MenuProvider>

            <Button asChild className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-2.5 h-auto text-sm font-medium tracking-wide transition-all duration-200 hover:-translate-y-px">
              <Link to="/HomePage">Start Your Project</Link>
            </Button>
          </div>

          {}
          <div className="flex md:hidden items-center">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open menu" className="text-foreground hover:bg-foreground/10 hover:text-foreground">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-full bg-background text-foreground border-l-0 p-0 flex flex-col">
                <SheetHeader className="px-6 pt-6 pb-4 border-b border-border">
                  <SheetTitle className="text-left">
                    <Link to="/HomePage" onClick={() => setMobileOpen(false)} aria-label="Acumen D&B home">
                      <WvcLogo className="h-7 w-auto" />
                    </Link>
                  </SheetTitle>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto pt-8">
                  <MenuProvider menu_id="16">
                    <MobileMenuConsumer onNavigate={() => setMobileOpen(false)} />
                  </MenuProvider>
                </div>

                <div className="px-6 pb-8 pt-6 border-t border-border">
                  <Button asChild className="w-full rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 py-3 h-auto text-base font-medium tracking-wide">
                    <Link to="/HomePage" onClick={() => setMobileOpen(false)}>
                      Start Your Project
                    </Link>
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}